import { ref, watch } from 'vue'
import { listConversations, type ConversationListItem } from '@/services/wootdashConversations'
import { listAgents, type CpAgent } from '@/services/wootdashAgents'
import { listTeams, listUnits, type CpTeam, type CpUnit } from '@/services/wootdashTeams'
import { onListChanged, onRealtimeRecovered } from '@/composables/useConversationRealtime'
import { isBlacklistedConversation, loadBlacklist } from '@/composables/useBlacklist'
import { hiddenFluxoIdsCp, isFluxoHiddenCp } from '@/composables/useHiddenFluxosCp'
import { toMediaUrl } from '@/utils/media'
import { agentAvatarUrl, seedAgentAvatars } from '@/composables/useAgentAvatars'
import { createCoalescedRunner } from '@/utils/coalescedRunner'
import type { ConversationCp, ConversationStatusCp } from '@/types/dashboardCp'

// Motor de dados do Dashboard real (WootDash) — substitui, pro que é WootDash,
// o papel que useDashboardEngine.ts (Chatwoot/n8n) cumpre; aquele não é tocado, continua
// servindo FocoView.vue/MobileView.vue sem mudança (fora de escopo desta fase).
//
// "conversations" aqui é só o conjunto ATIVO (Open+Pending, até ACTIVE_PAGE_SIZE linhas) —
// mesma ideia da "light cycle" do motor antigo (nunca reprocessa resolvida/fechada pra
// computar os cards/abas/agentes). As contagens totais por status vêm de 4 chamadas leves
// (tamanho=1, só lê o Total da paginação) em vez de trazer a base inteira pro cliente.
const ACTIVE_PAGE_SIZE = 500

// Janela mínima entre refetches disparados por evento realtime (gargalos-restantes.md item 4
// do WootDash) — ver utils/coalescedRunner.ts. Ações do próprio usuário (filtro, "Esconder
// Fluxo", troca de token) continuam chamando refetch() direto, sem esperar a janela.
export const REALTIME_REFETCH_MIN_INTERVAL_MS = 3000

const conversations = ref<ConversationCp[]>([])
const counts = ref({ open: 0, pending: 0, resolved: 0, closed: 0 })
const allAgents = ref<{ id: string; name: string; email: string; thumbnail: string; role: string; availability_status: string; is_observer?: boolean }[]>([])
const allTeams = ref<{ id: string; name: string; unitId: string; uf: string | null }[]>([])
const scopeReady = ref(false)
const dataStale = ref(false)

let currentToken = ''
let started = false
let unsubscribeListChanged: (() => void) | null = null
let stopRecovered: (() => void) | null = null
let agentsTeamsPollTimer: ReturnType<typeof setInterval> | null = null
// Cache dos agentes crus (só o que toConversationCp precisa pra resolver assignee) — separado
// de allAgents (já formatado pra exibição). Evita rebuscar agentes/times a cada mensagem (ver
// item 1 do plano-performance-escalabilidade.md): antes, refetch() rodava loadAgentsAndTeams()
// toda vez que qualquer conversa mudava em qualquer lugar do sistema — com N agentes
// conectados, isso sozinho já eram 3N requisições HTTP por mensagem (listAgents/listTeams/
// listUnits), a maioria delas buscando dado que não mudou.
let cachedAgents: CpAgent[] = []

// Filtro global do Dashboard (barra Time/Etiqueta/Fluxo/Status, ver DashboardView.vue) —
// afeta KPIs/abas/gráfico junto (não só a tabela), igual o filtro único que a versão antiga
// (Chatwoot) tinha. "Lista vazia de status" = sem restrição, mesma convenção já usada ali.
export interface DashboardGlobalFilters {
  teamId?: string
  tagId?: string
  flowId?: string
  statuses?: Array<'Open' | 'Pending' | 'Resolved' | 'Closed'>
  createdFrom?: string
  createdTo?: string
}
let globalFilters: DashboardGlobalFilters = {}

const STATUS_MAP: Record<number, ConversationStatusCp> = { 0: 'open', 1: 'pending', 2: 'resolved', 3: 'closed' }

function toEpochSeconds(iso: string | null): number {
  return iso ? Math.floor(new Date(iso).getTime() / 1000) : 0
}

function toConversationCp(item: ConversationListItem, agentIndex: Map<string, CpAgent>): ConversationCp {
  const status = STATUS_MAP[item.status] ?? 'open'
  const isActive = status === 'open' || status === 'pending'
  // Sem waiting_since no WootDash — reconstruído: conversa ativa cuja última mensagem
  // não veio do agente está esperando resposta desde aquela mensagem.
  const waitingSince = isActive && item.lastMessageAt && item.lastMessageSenderType !== 'Agent'
    ? toEpochSeconds(item.lastMessageAt)
    : 0
  const agent = item.agentId ? agentIndex.get(item.agentId) : undefined

  return {
    id: item.id,
    status,
    channel: 'Channel::Whatsapp',
    waiting_since: waitingSince,
    unread_count: 0,
    created_at: toEpochSeconds(item.createdAt),
    resolved_at: toEpochSeconds(item.resolvedAt),
    snoozed_until: null,
    contact: {
      id: item.contactId,
      name: item.contactName,
      phone: item.contactPhone ?? '',
      thumbnail: toMediaUrl(item.contactAvatarUrl) ?? '',
    },
    assignee: item.agentId ? {
      id: item.agentId,
      name: item.agentName ?? '',
      email: agent?.email ?? '',
      role: agent?.roleNames?.[0] ?? '',
      thumbnail: agentAvatarUrl(item.agentId) ?? toMediaUrl(agent?.avatarUrl ?? null) ?? '',
      availability: (agent?.status ?? 'Offline').toLowerCase(),
    } : null,
    team: item.teamId ? { id: item.teamId, name: item.teamName ?? '' } : null,
    labels: item.tagNames,
    fluxo_id: item.flowName ?? null,
    priority: null,
    cpf: null,
    cnpj: null,
  }
}

async function loadAgentsAndTeams() {
  const [{ agents }, teams, units] = await Promise.all([
    listAgents(currentToken),
    listTeams(currentToken),
    listUnits(currentToken),
  ])
  const unitIndex = new Map<string, CpUnit>(units.map(u => [u.id, u]))
  allAgents.value = agents.map(a => ({
    id: a.id,
    name: `${a.firstName} ${a.lastName}`.trim(),
    email: a.email,
    thumbnail: toMediaUrl(a.avatarUrl) ?? '',
    role: a.roleNames[0] ?? '',
    availability_status: a.status.toLowerCase(),
    is_observer: !!a.isObserver,
  }))
  allTeams.value = teams.map((t: CpTeam) => ({
    id: t.id,
    name: unitIndex.get(t.unitId) ? `${t.name} — ${unitIndex.get(t.unitId)!.name}` : t.name,
    unitId: t.unitId,
    uf: t.uf,
  }))
  cachedAgents = agents
  seedAgentAvatars(agents)
  return agents
}

async function refetch() {
  if (!currentToken) return
  try {
    const agentIndex = new Map(cachedAgents.map(a => [a.id, a]))

    const common = {
      teamId: globalFilters.teamId, tagId: globalFilters.tagId, flowId: globalFilters.flowId,
      createdFrom: globalFilters.createdFrom, createdTo: globalFilters.createdTo,
      // "Esconder Fluxo" (DashHeader.vue) no servidor — some também dos KPIs (counts.*).
      excludeFlowIds: hiddenFluxoIdsCp.value,
    }
    // Sem seleção de status = sem restrição (mesma convenção do filtro antigo) — com seleção,
    // um status fora da lista fica zerado (não é só "não destacado", some do KPI mesmo).
    const statusFilter = globalFilters.statuses?.length ? globalFilters.statuses : ['Open', 'Pending', 'Resolved', 'Closed'] as const
    const activeStatuses = statusFilter.filter(s => s === 'Open' || s === 'Pending')
    const zero = Promise.resolve({ conversations: [] as ConversationListItem[], total: 0 })

    const [active, openCount, pendingCount, resolvedCount, closedCount] = await Promise.all([
      activeStatuses.length
        ? listConversations(currentToken, { statuses: activeStatuses, pageSize: ACTIVE_PAGE_SIZE, ...common })
        : zero,
      statusFilter.includes('Open')     ? listConversations(currentToken, { statuses: ['Open'], pageSize: 1, ...common })     : zero,
      statusFilter.includes('Pending')  ? listConversations(currentToken, { statuses: ['Pending'], pageSize: 1, ...common })  : zero,
      statusFilter.includes('Resolved') ? listConversations(currentToken, { statuses: ['Resolved'], pageSize: 1, ...common }) : zero,
      statusFilter.includes('Closed')   ? listConversations(currentToken, { statuses: ['Closed'], pageSize: 1, ...common })   : zero,
    ])

    // isFluxoHiddenCp de novo aqui só por garantia (o servidor já excluiu) — a lista negra
    // continua sendo só client-side.
    conversations.value = active.conversations
      .filter(item => !isFluxoHiddenCp(item.flowId) && !isBlacklistedConversation(item))
      .map(item => toConversationCp(item, agentIndex))
    counts.value = {
      open: openCount.total,
      pending: pendingCount.total,
      resolved: resolvedCount.total,
      closed: closedCount.total,
    }
    dataStale.value = false
  } catch {
    dataStale.value = true
  } finally {
    scopeReady.value = true
  }
}

const realtimeRefetch = createCoalescedRunner(() => refetch(), REALTIME_REFETCH_MIN_INTERVAL_MS)

export function useWootDashLiveData() {
  return { conversations, counts, allAgents, allTeams, scopeReady, dataStale }
}

// Trocar "Esconder Fluxo" (DashHeader.vue) refaz a busca na hora, sem esperar o próximo
// evento realtime/poll — mesmo dado já cacheado no backend, só reaplica o filtro local.
watch(hiddenFluxoIdsCp, () => { if (started) refetch() })

/** Chamado pela barra de filtro global do Dashboard — refaz a busca com o escopo novo. */
export function setDashboardGlobalFilters(filters: DashboardGlobalFilters): void {
  globalFilters = filters
  refetch()
}

/** Idempotente — chamar de novo com o motor já rodando só atualiza o token e refaz a busca. */
export function startWootDashLiveData(token: string): void {
  currentToken = token
  // Lista negra: carrega antes da próxima busca pra já esconder os bloqueados.
  loadBlacklist(token).then(() => { if (started) refetch() }).catch(() => { /* sem lista, a etiqueta ainda esconde */ })
  if (started) { refetch(); return }
  started = true
  // Agentes/times carregam uma vez no início e depois só no timer próprio (5min) — não mais
  // a cada mensagem (ver comentário em cachedAgents). loadAgentsAndTeams() antes do primeiro
  // refetch() pra já ter o agentIndex pronto na primeira renderização.
  loadAgentsAndTeams().then(refetch)
  // Admin recebe todo evento do sistema (grupo "painel") — coalescido, não 1 refetch por evento.
  unsubscribeListChanged = onListChanged(() => realtimeRefetch.trigger())
  // Sem polling: o merge incremental cobre o dia a dia, e o que se perder numa queda da conexão é rebuscado
  // quando ela volta (onRealtimeRecovered) — substitui o refetch a cada 60 s que existia só pra cobrir isso.
  stopRecovered = onRealtimeRecovered(() => refetch())
  agentsTeamsPollTimer = setInterval(loadAgentsAndTeams, 5 * 60_000)
}

export function stopWootDashLiveData(): void {
  started = false
  currentToken = ''
  conversations.value = []
  allAgents.value = []
  allTeams.value = []
  cachedAgents = []
  scopeReady.value = false
  globalFilters = {}
  unsubscribeListChanged?.()
  unsubscribeListChanged = null
  realtimeRefetch.cancel()
  stopRecovered?.()
  stopRecovered = null
  if (agentsTeamsPollTimer) { clearInterval(agentsTeamsPollTimer); agentsTeamsPollTimer = null }
}
